import { Close, FolderAdd } from '@carbon/react/icons';
import {
  Button,
  ComboBox,
  Form,
  InlineNotification,
  Loading,
  Modal,
  RadioButton,
  RadioButtonGroup,
  Search,
  TextArea,
  TextInput,
  Tile,
  Layer,
} from '@carbon/react';
import { Map } from 'components/map';

import { PageHeader } from 'components/page-header';
import { useAtom, useAtomValue } from 'jotai';
import { createResourceAtom } from 'lib/atoms';
import { debounce } from 'lodash-es';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { GeologicIntervalResponse } from 'types/api-responses';
import style from './add-structure-grid.module.scss';
import {
  createStructureGridFileAtom,
  structureGridFileAtom,
  usePreviewAtom,
} from './atoms';
import { EpsgProjectionMap } from '../epsg-projection-map/epsg-projection-map';
import {
  getBasinIntervals,
  getOutsideUsWarning,
  structureGridMapConfigAtom,
} from './utils';
import {
  getUniqueBasinNames,
  getSelectedIntervalId,
} from 'pages/structure-grids/util';

interface ProjectionGeometry {
  min_x: number;
  max_x: number;
  min_y: number;
  max_y: number;
}

export interface PreviewData {
  boundingBox: ProjectionGeometry | { error: string };
  /**
   * Whether the file, read in the chosen projection, reaches the continental US. Null when
   * the bounding box could not be calculated.
   */
  overlapsConus: boolean | null;
}

interface ProjectionOption {
  srid: number;
  projectionName: string;
  geometry: ProjectionGeometry;
}

interface EpsgCodeResponse {
  results: Array<{
    srid: number;
    projectionName: string;
    geometry: ProjectionGeometry;
  }>;
  meta: {
    count: number;
    pageSize: number;
  };
}

interface CreateStructureGridData {
  raw_surface: string;
  grid_surface: string;
  source_projection: string;
  owner: string;
  source: string;
  geologic_interval_id: number;
  notes?: string;
}

const ProjectionModal = ({
  open,
  onClose,
  onSelect,
  setSelectedProjection,
  signedId,
}: {
  open: boolean;
  onClose: () => void;
  onSelect: (projection: ProjectionOption) => void;
  setSelectedProjection: (projection: ProjectionOption | null) => void;
  signedId: string | null;
}) => {
  const [epsgSearchQuery, setEpsgSearchQuery] = useState('');
  const [epsgSearchUrl, setEpsgSearchUrl] = useState<string | undefined>(
    undefined
  );
  const [tempSelectedProjection, setTempSelectedProjection] =
    useState<ProjectionOption | null>(null);
  const [epsgPreviewData, setEpsgPreviewData] = useState<PreviewData | null>(
    null
  );
  const [previewError, setPreviewError] = useState<string | null>(null);

  const epsgSearchAtom = useMemo(
    () => createResourceAtom<EpsgCodeResponse>(epsgSearchUrl),
    [epsgSearchUrl]
  );

  const previewHref = tempSelectedProjection
    ? `/structure_grid_surface_blobs/${signedId}/preview?projection=EPSG:${tempSelectedProjection.srid}`
    : undefined;

  const previewAtom = usePreviewAtom(previewHref);
  const previewData = useAtomValue(previewAtom);

  useEffect(() => {
    if (previewData?.data && !('error' in previewData.data)) {
      setEpsgPreviewData(previewData.data);
      setPreviewError(null);
    } else if (previewData?.data && 'error' in previewData.data) {
      setPreviewError('Failed to load preview data');
      setEpsgPreviewData(null);
    }
  }, [previewData]);

  const epsgSearchData = useAtomValue(epsgSearchAtom);
  const searchResults =
    epsgSearchData?.data && !('error' in epsgSearchData.data)
      ? epsgSearchData.data.results
      : [];

  // Create a debounced search function
  const debouncedSearch = useMemo(
    () =>
      debounce((query: string) => {
        if (query.length > 0) {
          setEpsgSearchUrl(
            `/queries/epsg_search?query=${encodeURIComponent(query)}`
          );
        } else {
          setEpsgSearchUrl(undefined);
        }
      }, 400),
    []
  );

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    debouncedSearch(epsgSearchQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [epsgSearchQuery]);

  // Clear search when modal closes
  useEffect(() => {
    if (!open) {
      setEpsgSearchQuery('');
      setEpsgSearchUrl(undefined);
      setTempSelectedProjection(null);
      setEpsgPreviewData(null);
      setPreviewError(null);
    }
  }, [open]);

  // Clear selected projection when search results become empty
  useEffect(() => {
    if (searchResults.length === 0) {
      setTempSelectedProjection(null);
      setEpsgPreviewData(null);
      setPreviewError(null);
    }
  }, [searchResults.length]);

  const handleConfirm = () => {
    if (tempSelectedProjection) {
      setSelectedProjection(tempSelectedProjection);
      onSelect(tempSelectedProjection);
    }
    onClose();
  };

  const currentPreview =
    !previewData?.loading && previewData?.data && !('error' in previewData.data)
      ? previewData.data
      : null;
  const outsideUsWarning = getOutsideUsWarning(currentPreview);

  return (
    <Modal
      modalHeading="Add Projection"
      open={open}
      onRequestClose={onClose}
      size="lg"
      primaryButtonText="Confirm"
      secondaryButtonText="Cancel"
      onRequestSubmit={handleConfirm}
    >
      <div className={style.projectionModal}>
        {previewError && (
          <InlineNotification
            kind="error"
            title=""
            subtitle={previewError}
            lowContrast
          />
        )}
        <h4 className={style.searchingText}>
          Searching all available latitude and longitudes
        </h4>
        {currentPreview &&
          currentPreview.boundingBox &&
          'error' in currentPreview.boundingBox && (
            <InlineNotification
              kind="error"
              title=""
              subtitle={'Failed to load uploaded file preview data'}
              lowContrast
            />
          )}
        {outsideUsWarning && (
          <InlineNotification
            kind="warning"
            title=""
            subtitle={outsideUsWarning}
            lowContrast
          />
        )}
        <div className={style.contentSection}>
          <div className={style.projectionList}>
            <h5>EPSG</h5>
            <div className={style.searchSection}>
              <Search
                labelText="Search"
                placeholder="Search input text"
                value={epsgSearchQuery}
                onChange={(e) => setEpsgSearchQuery(e.target.value)}
                size="sm"
              />
            </div>
            {epsgSearchData?.loading && epsgSearchUrl && epsgSearchQuery ? (
              <div className={style.loadingContainer}>
                <Loading
                  description="Loading EPSG codes..."
                  small
                  withOverlay={false}
                />
              </div>
            ) : !epsgSearchData?.loading && searchResults.length === 0 ? (
              <div className={style.noResults}>No EPSG codes available</div>
            ) : (
              <div className={style.radioButtonContainer}>
                <RadioButtonGroup
                  name="projection-selection"
                  orientation="vertical"
                  onChange={(value: string | number | undefined) => {
                    const selected = searchResults.find(
                      (p: { projectionName: string }) =>
                        p.projectionName === value
                    );
                    if (selected) {
                      setTempSelectedProjection({
                        srid: selected.srid,
                        projectionName: selected.projectionName,
                        geometry: selected.geometry,
                      });
                    }
                  }}
                  valueSelected={tempSelectedProjection?.projectionName}
                >
                  {searchResults.map(
                    (proj: {
                      srid: number;
                      projectionName: string;
                      geometry: ProjectionGeometry;
                    }) => (
                      <RadioButton
                        key={proj.srid}
                        id={proj.projectionName}
                        labelText={`${proj.srid} - ${proj.projectionName}`}
                        value={proj.projectionName}
                      />
                    )
                  )}
                </RadioButtonGroup>
              </div>
            )}
          </div>
          <div className={style.mapSection}>
            <h5>Map</h5>
            <div className={style.map}>
              {tempSelectedProjection ? (
                previewData?.loading ? (
                  <div className={style.mapLoadingContainer}>
                    <Loading
                      description="Loading preview data..."
                      withOverlay={false}
                    />
                  </div>
                ) : (
                  <EpsgProjectionMap
                    geometry={tempSelectedProjection.geometry}
                    previewData={
                      epsgPreviewData &&
                      !('error' in epsgPreviewData.boundingBox)
                        ? epsgPreviewData.boundingBox
                        : undefined
                    }
                    mapId={tempSelectedProjection.srid.toString()}
                  />
                )
              ) : (
                <Map
                  longitude={-98}
                  latitude={39}
                  zoom={4}
                  showControls={true}
                  showMapFilters={false}
                  mapConfigAtom={structureGridMapConfigAtom}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

const AddStructureGridPage = () => {
  const history = useHistory();
  const geoLogicalIntervalsAtom = useMemo(
    () => createResourceAtom<GeologicIntervalResponse>('/geologic_intervals'),
    []
  );

  const geoLogicalIntervalsData = useAtomValue(geoLogicalIntervalsAtom);
  const [selectedBasin, setSelectedBasin] = useState<string | null>(null);
  const [selectedInterval, setSelectedInterval] = useState<string | null>(null);
  const [isProjectionModalOpen, setIsProjectionModalOpen] = useState(false);
  const [selectedProjection, setSelectedProjection] =
    useState<ProjectionOption | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dataSource, setDataSource] = useState<string>('');
  const [dataOwner, setDataOwner] = useState<string>('');
  const [gridSurface, setGridSurface] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [errors, setErrors] = useState<string[]>([]);
  const [signedId, setSignedId] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [upload, setUpload] = useAtom(structureGridFileAtom);
  const [result, createFile] = useAtom(createStructureGridFileAtom);

  useEffect(() => {
    resetFileUpload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Compute interval items based on selected basin
  const intervalItems = useMemo(() => {
    if (
      !selectedBasin ||
      !geoLogicalIntervalsData?.data ||
      'error' in geoLogicalIntervalsData.data
    )
      return [];
    const data = geoLogicalIntervalsData.data;
    const uniqueBasins = getUniqueBasinNames(data);
    const geologicIntervals = getBasinIntervals(data, uniqueBasins);
    return geologicIntervals[selectedBasin] || [];
  }, [selectedBasin, geoLogicalIntervalsData?.data]);

  useEffect(() => {
    setSelectedInterval(null);
  }, [selectedBasin]);

  // Clear projection when basin or interval changes
  useEffect(() => {
    setSelectedProjection(null);
  }, [selectedBasin, selectedInterval]);

  const handleIntervalSelect = (e: any) => {
    setSelectedInterval(e.selectedItem);
    if (e.selectedItem) {
      setIsProjectionModalOpen(true);
    }
  };

  const handleProjectionSelect = (projection: ProjectionOption) => {
    setSelectedProjection(projection);
  };

  const resetFileUpload = () => {
    setUpload(undefined);
    setUploadProgress(0);
    setErrors([]);
    setSignedId(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setSelectedBasin(null);
    setSelectedInterval(null);
    setSelectedProjection(null);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      setUploadProgress(0);
      setErrors([]);
      setUpload(files[0]);
    }
  };
  useEffect(() => {
    if (upload?.status === 'uploading') {
      setUploadProgress(0);
      const interval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 90) {
            clearInterval(interval);
            return prev;
          }
          return prev + 5;
        });
      }, 300);

      return () => clearInterval(interval);
    } else if (upload?.status === 'complete') {
      setUploadProgress(100);
    } else {
      setUploadProgress(0);
    }
    return undefined;
  }, [upload?.status]);

  // Watch for file changes from direct upload
  useEffect(() => {
    if (upload?.status === 'complete' && upload.file) {
      setSignedId(upload.file);
    }
  }, [upload?.status, upload?.file]);

  // Fetch preview data when signed_id changes

  const handleSubmit = () => {
    if (!upload?.file) {
      setErrors(['Please upload a file']);
      return;
    }

    const intervalId = getSelectedIntervalId(selectedInterval, intervalItems);
    if (!intervalId) {
      setErrors(['Please select a valid geologic interval']);
      return;
    }

    const data: CreateStructureGridData = {
      raw_surface: upload.file,
      source_projection: `EPSG:${selectedProjection?.srid}`,
      source: dataSource,
      grid_surface: gridSurface,
      owner: dataOwner,
      geologic_interval_id: intervalId,
      notes: notes,
    };

    createFile({
      url: '/structure_grids',
      type: 'POST',
      data,
      onSuccess: () => {
        history.push('/structure_grids');
      },
      onError: (error) => {
        setErrors([error?.error ?? 'Error uploading structure grid']);
      },
    });
  };

  if (geoLogicalIntervalsData?.loading) {
    return (
      <div className={style.loadingContainer}>
        <Loading
          description="Loading geological intervals..."
          small
          withOverlay={false}
        />
      </div>
    );
  }

  if (
    !geoLogicalIntervalsData?.data ||
    'error' in geoLogicalIntervalsData.data
  ) {
    return (
      <InlineNotification
        kind="error"
        lowContrast
        title="Error"
        subtitle="Unable to fetch geological intervals"
        className={style.errorNotification}
      />
    );
  }

  const data = geoLogicalIntervalsData.data;
  const uniqueBasins = getUniqueBasinNames(data);

  return (
    <>
      <PageHeader
        title="Add Structure Grid"
        breadcrumbs={[
          ['structure_grids', 'Structure Grid Management'],
          ['add_structure_grids', 'Add Structure Grid'],
        ]}
        actions={<div />}
      />
      <ProjectionModal
        open={isProjectionModalOpen}
        onClose={() => setIsProjectionModalOpen(false)}
        onSelect={handleProjectionSelect}
        setSelectedProjection={setSelectedProjection}
        signedId={signedId}
      />
      <div className={style.addStructureGridContainer}>
        <Tile className={style.tile}>
          <Form>
            {/* Upload Section */}
            <div className={style.section}>
              <div className={style.sectionHeader}>
                <FolderAdd />
                <label htmlFor="uploadFile" className={style.checkboxLabel}>
                  Upload a File
                </label>
              </div>

              <div className={style.uploadContent}>
                <div className={style.requiredField}>
                  Upload file* (Required)
                </div>
                <div className={style.uploadDescription}>
                  Upload a single .tif, .asc, .dat, .xyz or .tiff file
                </div>
                <div className={style.uploadButtonContainer}>
                  <input
                    type="file"
                    ref={fileInputRef}
                    data-testid="file-input"
                    onChange={handleFileChange}
                    accept=".tif,.asc,.tiff,.dat,.xyz"
                    style={{ display: 'none' }}
                  />
                  <Button
                    className={style.uploadButton}
                    onClick={() => fileInputRef.current?.click()}
                    disabled={!!upload?.name}
                  >
                    Upload
                  </Button>
                  {upload?.name && (
                    <div className={style.fileStatus} data-testid="file-status">
                      <div className={style.fileInfo}>
                        <span className={style.fileName}>{upload.name}</span>
                        {upload?.status === 'uploading' && (
                          <div className={style.progressContainer}>
                            <div className={style.progressLabel}>
                              {'Uploading...'}
                            </div>
                            <div className={style.progressBar}>
                              <div
                                className={style.progressBarFill}
                                style={{ width: `${uploadProgress}%` }}
                                role="progressbar"
                                aria-valuenow={uploadProgress}
                                aria-valuemin={0}
                                aria-valuemax={100}
                              />
                            </div>
                            <div className={style.progressText}>
                              {uploadProgress}%
                            </div>
                          </div>
                        )}
                        {upload?.status === 'complete' && (
                          <span className={style.successText}>
                            Upload complete
                          </span>
                        )}
                        <Button
                          kind="ghost"
                          size="sm"
                          iconDescription="Remove file"
                          onClick={resetFileUpload}
                          hasIconOnly
                          renderIcon={Close}
                        />
                      </div>
                      {upload.invalid && (
                        <div className={style.errorText}>{upload.error}</div>
                      )}
                    </div>
                  )}
                  {!!errors.length && (
                    <InlineNotification
                      kind={'error'}
                      lowContrast
                      title={'Error!'}
                      subtitle={`${errors.join(', ')}.`}
                    />
                  )}
                </div>
                <div className={style.formField}>
                  <Layer>
                    <TextInput
                      id="gridSurface"
                      labelText="Grid Surface"
                      value={gridSurface}
                      onChange={(e) => setGridSurface(e.target.value)}
                    />
                  </Layer>
                </div>
              </div>
            </div>

            {/* Geographic Information Section */}
            <div className={style.section}>
              <div className={style.sectionHeader}>
                <h3 className={style.sectionTitle}>
                  {upload?.file
                    ? 'File MetaData'
                    : 'Assign Geographic Information'}
                </h3>
                {upload?.file && (
                  <div className={style.requiredFieldsNote}>
                    All fields below are required
                  </div>
                )}
              </div>
              <div className={style.formContent}>
                <div className={style.formField}>
                  <Layer>
                    <ComboBox
                      id="basin"
                      titleText="Basin"
                      placeholder="Select Basin"
                      items={uniqueBasins}
                      selectedItem={selectedBasin}
                      onChange={(e: any) =>
                        setSelectedBasin(e.selectedItem ?? null)
                      }
                      disabled={!upload?.file}
                    />
                  </Layer>
                </div>
                <div className={style.formField}>
                  <Layer>
                    <ComboBox
                      id="geologicInterval"
                      titleText="Geologic Interval"
                      placeholder="Select"
                      items={intervalItems.map(
                        (interval) => interval.geologicInterval
                      )}
                      selectedItem={selectedInterval}
                      onChange={handleIntervalSelect}
                      itemToString={(item) => item || ''}
                      disabled={!selectedBasin}
                    />
                  </Layer>
                </div>
                <div className={style.formField}>
                  <Layer>
                    <TextInput
                      id="gridProjection"
                      labelText="Projection"
                      value={selectedProjection?.projectionName || ''}
                      readOnly
                    />
                  </Layer>
                </div>
              </div>
            </div>

            {/* Details Section */}
            <div className={style.section}>
              <h3 className={style.sectionTitle}>Details</h3>
              <div className={style.formContent}>
                <div className={style.formRow}>
                  <Layer>
                    <TextInput
                      id="dataSource"
                      labelText="Data Source"
                      className={style.textInput}
                      value={dataSource}
                      onChange={(e) => setDataSource(e.target.value)}
                    />
                  </Layer>
                  <Layer>
                    <TextInput
                      id="dataOwner"
                      labelText="Data Owner"
                      className={style.textInput}
                      value={dataOwner}
                      onChange={(e) => setDataOwner(e.target.value)}
                    />
                  </Layer>
                </div>
                <div className={style.formField}>
                  <Layer>
                    <TextArea
                      id="notes"
                      labelText="Notes (Optional)"
                      placeholder="Notes for structure grid upload"
                      rows={4}
                      className={style.textArea}
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                    />
                  </Layer>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className={style.actionButtons}>
              <Button
                kind="primary"
                className={style.submitButton}
                onClick={handleSubmit}
                disabled={
                  !upload?.file ||
                  result.loading ||
                  !selectedBasin ||
                  !selectedInterval ||
                  !selectedProjection ||
                  !dataSource ||
                  !dataOwner ||
                  !gridSurface
                }
              >
                Submit Structure Grid
              </Button>
              <Button
                kind="secondary"
                onClick={() => history.push('/structure_grids')}
                className={style.cancelButton}
              >
                Cancel
              </Button>
            </div>
          </Form>
        </Tile>
      </div>
    </>
  );
};

export default AddStructureGridPage;
